import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChartHeader from 'components/charts/ChartHeader';
import DistributionList from 'components/charts/DistributionList';
import DoughnutChart from 'components/charts/chartJS/DoughnutChart';
import { chartColors } from 'components/charts/chartJS/utils';
import Button from 'components/field/Button';
import DateFilter from 'components/field/DateFilter';
import Card from 'components/global/Card';
import Icon from 'components/global/Icon';
import ManageWidgetsModal from 'components/global/ManageWidgetsModal';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { DeliveryCompDistribution } from 'pages/Analysis/DeliveryCompanyTab/DeliveryCompany';
import { chartsConfig } from 'pages/Analysis/analysisWidgetsConfig';
import {
	ClosedOrders,
	OrderDistributionDayly,
	OrderDistributionWeekly,
	ProcessingOrders
} from 'pages/Dashboard/Dashboard';

import { REPORT } from 'services/consts';

import { faCircleInfo, faObjectsColumn } from '@fortawesome/pro-regular-svg-icons';

import * as S from './Metrics.styled';
import { homePageChartsConfigRaw } from './homePageChartsConfig';

const Metrics = () => {
	const data = chartsConfig(homePageChartsConfigRaw);

	return (
		<S.MetricsSectionWrapper>
			<S.SectionHeader>
				<S.Title>
					<Typography variant="p2" fontWeight="700" noWrap>
						metrics
					</Typography>
					<Tooltip placement="right" title={'metricsTooltip'}>
						<Icon icon={faCircleInfo} />
					</Tooltip>
				</S.Title>

				<Button
					icon={faObjectsColumn}
					label={'manageWidgets.buttonLabel'}
					variant="blue"
					onClick={() =>
						NiceModal.show(ManageWidgetsModal, {
							data
						})
					}
				/>
			</S.SectionHeader>
			<S.WidgetContainer>
				<WidgetPageContainer>
					<WidgetWrapper widgetId={'pendingPreparation'}>
						<PendingPreparation />
					</WidgetWrapper>
					<WidgetWrapper widgetId={'pendingPickup'}>
						<PendingPickup />
					</WidgetWrapper>
					<WidgetWrapper widgetId={'pendingDelivery'}>
						<PendingDelivery />
					</WidgetWrapper>
					<WidgetWrapper widgetId={'currentOrders'}>
						<ClosedOrders />
					</WidgetWrapper>
					<WidgetWrapper widgetId={'processingOrders'}>
						<ProcessingOrders />
					</WidgetWrapper>
					<WidgetWrapper widgetId={'deliveryCompDistribution'}>
						<DeliveryCompDistribution />
					</WidgetWrapper>
					<WidgetWrapper widgetId={'dashboard.orderDistributionWeekly'}>
						<OrderDistributionWeekly />
					</WidgetWrapper>
					<WidgetWrapper widgetId={'dashboard.orderDistributionDaily'}>
						<OrderDistributionDayly />
					</WidgetWrapper>
					<WidgetWrapper widgetId={'topDestinations'}>
						<DistributionList
							fetchURL={REPORT.DASHBOARD.GET_TOP_DESTINATION_POINTS}
							title="topDestinations"
							filtered
							defaultDay="last30Days"
						/>
					</WidgetWrapper>
					<WidgetWrapper widgetId={'deliveryCompDistributionDashboard'}>
						<DistributionList
							title="deliveryCompDistribution"
							defaultDay="last30Days"
							filtered
							fetchURL={REPORT.DASHBOARD.GET_SHIPPING_PARTNERS_DISTRIBUTION}
							variant="with-logo"
						/>
					</WidgetWrapper>
					<WidgetWrapper widgetId={'saleChannelDistribution'}>
						<DistributionList
							title="saleChannelDistribution"
							defaultDay="last30Days"
							filtered
							fetchURL={REPORT.DASHBOARD.GET_SALES_CHANNEL_DISTRIBUTION}
						/>
					</WidgetWrapper>
				</WidgetPageContainer>
			</S.WidgetContainer>
		</S.MetricsSectionWrapper>
	);
};

const parseData = (dataInput, t) => ({
	labels: dataInput.map((item) => t(item.label)),
	datasets: [
		{
			data: dataInput.map((item) => parseInt(item.data)),
			backgroundColor: chartColors.backgroundColor,
			borderColor: 'white'
		}
	]
});

const PendingPreparation = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(7, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { t } = useTranslation();
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_CURRENT_PREPARATION_ORDERS,
		parseData: (data) => parseData(data, t),
		body
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={'pendingPreparation'}
				filter={<DateFilter defaultDay={'last7Days'} setValue={setFilter} />}
			/>
			<DoughnutChart data={data} innerTitle={'totalOrders'} loading={loading} />
		</Card>
	);
};

const PendingPickup = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(7, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { t } = useTranslation();
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_CURRENT_PICKUP_ORDERS,
		parseData: (data) => parseData(data, t),
		body
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={'pendingPickup'}
				filter={<DateFilter defaultDay={'last7Days'} setValue={setFilter} />}
			/>
			<DoughnutChart data={data} innerTitle={'totalOrders'} loading={loading} />
		</Card>
	);
};

const PendingDelivery = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(7, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { t } = useTranslation();
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_CURRENT_DELIVERY_ORDERS,
		parseData: (data) => parseData(data, t),
		body
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={'pendingDelivery'}
				filter={<DateFilter defaultDay={'last7Days'} setValue={setFilter} />}
			/>
			<DoughnutChart data={data} innerTitle={'totalOrders'} loading={loading} />
		</Card>
	);
};

export default Metrics;
